import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  AccountClearPart,
  AccountClearResult,
  AccountLookupResponse,
  BanStateFilter,
  BansResponse,
  ClearPart,
  ClearResult,
  EventsResponse,
  LimitScope,
  LimitState,
  LimitsResponse,
  LookupResponse,
  RateLimitAuditRow,
  RateLimitOverview,
} from '@/client/types/rateLimit';

const BASE = '/api/admin/v2/rate-limit';

const qs = (params: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const out = search.toString();
  return out ? `?${out}` : '';
};

export const rateLimitKeys = { all: ['rate-limit-v2'] as const };

/** Admin v2 wraps payloads in `{ status, data }`; accept either shape. */
const unwrap = <T>(raw: unknown): T => {
  if (raw != null && typeof raw === 'object' && 'data' in raw && 'status' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
};

/** One read. Lockouts count down by the second, so live reads come back every 10 s. */
const useRead = <T>(path: string, live = true, enabled = true) => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (): Promise<unknown> => unwrap<unknown>(await request<unknown>(`${BASE}${path}`)),
    'Failed to load rate limits',
  ) as unknown as () => Promise<T>;
  return useQuery({
    queryKey: [...rateLimitKeys.all, path],
    queryFn: fn,
    enabled,
    staleTime: live ? 5_000 : 60_000,
    refetchInterval: live ? 10_000 : false,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useRateLimitOverviewQuery = (enabled = true) =>
  useRead<RateLimitOverview>('/overview', true, enabled);

export const useRateLimitLimitsQuery = (
  f: { scope: LimitScope; state: LimitState; q?: string },
  enabled = true,
) => useRead<LimitsResponse>(`/limits${qs({ ...f, limit: 500 })}`, true, enabled);

export const useRateLimitBansQuery = (f: { state: BanStateFilter; q?: string }, enabled = true) =>
  useRead<BansResponse>(`/bans${qs({ ...f, limit: 500 })}`, true, enabled);

/** One client, by what the admin typed (IP / opaque key) or by `client_id` from a list. */
export const useRateLimitLookupQuery = (who: { client?: string; client_id?: string }) =>
  useRead<LookupResponse>(`/lookup${qs(who)}`, true, !!(who.client || who.client_id));

export const useRateLimitEventsQuery = (
  f: { event?: string; q?: string; since?: number },
  enabled = true,
) => useRead<EventsResponse>(`/events${qs({ ...f, limit: 300 })}`, true, enabled);

export const useRateLimitAuditQuery = (enabled = true) =>
  useRead<{ audit: RateLimitAuditRow[] }>(`/audit${qs({ limit: 200 })}`, false, enabled);

export const useRateLimitRefresh = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: rateLimitKeys.all });
};

export const useRateLimitClearMutation = () => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  const fn = withErrorHandling(
    async (v: {
      client?: string;
      client_id?: string;
      what: ClearPart[];
      endpoint?: string;
      reason: string;
    }): Promise<ClearResult> =>
      unwrap<ClearResult>(await request<unknown>(`${BASE}/clear`, 'POST', v)),
    'Nothing was cleared',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rateLimitKeys.all }),
  });
};

/** Everything stopping one person signing in, from what support knows: email, username or phone. */
export const useRateLimitAccountQuery = (q: string) =>
  useRead<AccountLookupResponse>(`/account${qs({ q })}`, true, !!q);

export const useRateLimitAccountClearMutation = () => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  const fn = withErrorHandling(
    async (v: {
      account: string;
      what: AccountClearPart[];
      ips?: string[];
      reason: string;
    }): Promise<AccountClearResult> =>
      unwrap<AccountClearResult>(await request<unknown>(`${BASE}/account/clear`, 'POST', v)),
    'Nothing was cleared',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rateLimitKeys.all }),
  });
};
